import Dotdotdot from 'react-dotdotdot';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { PillsList } from '@hivebrite/design-system/PillsList';

import { useListContext } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/contexts/ListContext';
import type { NewsWrapperProps } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/NewsWrapper';
import type { ListLayoutsDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/types';
import type { News } from 'hb-builder/components/BuilderComponents/dynamic/types/news';
import { goToModuleDetailPage } from 'hb-builder/utils/goToModuleDetailPage';
import ArticleText from 'hb-react/shared/apps/Articles/ArticlePublished/ArticleText';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';

const ListItem = ({
  dynamicAttributes,
  ids,
  isBackOffice,
  navigate,
  news,
  whiteLabelUrl,
}: Pick<
  NewsWrapperProps,
  'navigate' | 'isBackOffice' | 'ids' | 'whiteLabelUrl'
> & {
  dynamicAttributes?: ListLayoutsDynamicAttributes;
  news: News;
}) => {
  const {
    id,
    content,
    new_cover_picture: newCoverPicture,
    external_url: url,
    published_at: publishedAt,
    seo_description: seoDescription,
    subject,
    tags,
  } = news;

  const description = seoDescription || content;
  const {
    displayDescription,
    displayImage,
    displayDate,
    displayTags,
    displayTextLink,
    displayTitle,
  } = dynamicAttributes?.fields || {};

  const {
    setHoverEffectImage,
    hoverEffectImage,
    hoverEffectTitle,
    handleMouseEnterInTitle,
    handleMouseLeaveInTitle,
  } = useListContext();

  const handleMouseEnterInImage = (cardId) =>
    setHoverEffectImage({ cardId, isHover: true });

  const handleMouseLeaveInImage = () =>
    setHoverEffectImage({ cardId: null, isHover: false });

  const hasTags = displayTags && tags.length > 0;

  return (
    <Wrapper>
      {displayImage && (
        <ImgWrapper>
          <StyledImg
            $hoverOnTitle={hoverEffectTitle.isHover}
            $isRightCard={hoverEffectTitle.cardId === id}
            alt="cover picture"
            data-testid="builder-cover-wrapper"
            onClick={() =>
              ids &&
              navigate &&
              goToModuleDetailPage({
                ids,
                isBackOffice,
                navigate,
                news,
                whiteLabelUrl,
              })
            }
            onMouseEnter={handleMouseEnterInImage}
            onMouseLeave={handleMouseLeaveInImage}
            src={newCoverPicture.medium_url}
          />
        </ImgWrapper>
      )}
      <Box flex={1} flexDirection="column">
        <Box flexDirection="column">
          {displayTitle && (
            <HeaderTitleStyled
              $hoverOnImage={hoverEffectImage?.isHover}
              $isRightCard={hoverEffectImage?.cardId === id}
              onClick={() =>
                ids &&
                navigate &&
                goToModuleDetailPage({
                  ids,
                  isBackOffice,
                  navigate,
                  news,
                  whiteLabelUrl,
                })
              }
              onMouseEnter={() => handleMouseEnterInTitle(id)}
              onMouseLeave={() => handleMouseLeaveInTitle()}
            >
              <ClampedLinkWorkaround
                clamp={2}
                lineHeight="3rem"
                margin={-2}
                padding={2}
                useNativeClamp
              >
                {subject}
              </ClampedLinkWorkaround>
            </HeaderTitleStyled>
          )}

          {displayDate && (
            <DateStyled mb={!hasTags ? '10px' : ''}>
              {__('Published on %{date}').i__({
                date: publishedAt,
              })}
            </DateStyled>
          )}
        </Box>

        {hasTags && (
          <Box mb={2} mt={3}>
            <PillsList
              pills={tags}
              seeLessLabel={s__('Tags|See less')}
              seeMoreLabel={s__('Tags|See more')}
            />
          </Box>
        )}
        {displayDescription && description && (
          <DescriptionWrapper>
            <Dotdotdot clamp={2} useNativeClamp>
              {/* When iframes are present, the new ConsentGuard component gets displayed but when the 
                  user allows the content, Dotdotdot clamp the iframe and hides it. Simply not rendering
                  the iframes gives a more consistent user experience. */}
              <ArticleText text={description} noIframes />
            </Dotdotdot>
          </DescriptionWrapper>
        )}
        {displayTextLink && url && (
          <Box color="primary" as="a" href={isBackOffice ? '#' : url}>
            {s__('Builder|Continue reading in website')}
          </Box>
        )}
      </Box>
    </Wrapper>
  );
};

export default ListItem;

const Wrapper = styled(Box).attrs(() => ({
  display: 'flex',
  flexDirection: {
    _: 'column',
    m: 'row',
  },
  mb: {
    _: 6,
    l: 8,
  },
}))`
  width: 100%;
`;

const StyledImg = styled.img`
  cursor: pointer;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
  width: 100%;
  ${({
    $hoverOnTitle,
    $isRightCard,
  }: {
    $hoverOnTitle: boolean;
    $isRightCard: boolean;
  }) =>
    $hoverOnTitle &&
    $isRightCard &&
    css`
      transform: scale(1.1) !important;
    `}
  &:hover {
    transform: scale(1.1) !important;
  }
`;

const HeaderTitleStyled = styled(Box).attrs({
  font: 'h1-semibold',
  mb: {
    _: 1,
    l: '6px',
  },
  display: 'block',
})<{
  $hoverOnImage: boolean;
  $isRightCard: boolean;
}>`
  ${({ $hoverOnImage, $isRightCard }) =>
    $hoverOnImage &&
    $isRightCard &&
    css`
       {
        text-decoration: underline;
        color: ${({ theme }) => theme.colors.link};
      }
    `}
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.link} !important;
  }
`;

const DescriptionWrapper = styled(Box).attrs(() => ({
  fontSize: '14px',
  mb: 1,
}))`
  iframe,
  img,
  video {
    display: none;
  }
`;

const ImgWrapper = styled(Box).attrs(() => ({
  height: { _: '128px', m: '85px', l: '114px', xl: '122px' },
  mb: { _: 3, m: 0 },
  mr: { m: '20px', l: 6 },
  overflow: 'hidden',
  width: { _: '343px', m: '226px', l: '304px', xl: '327px' },
}))`
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const DateStyled = styled(Box).attrs({
  font: 'body-regular-asphalt',
  display: 'block',
  fontSize: '14px',
})``;
